import _ from 'lodash';
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import {connect} from 'react-redux';

import {
  CleanHtml,
  iSolemnlySwearThisHtmlIsClean,
} from '../../../../../../libs/shared/core/src/helpers/typescript/cleanHtml';
import {
  buildContactForHandle,
  ContactSourcesEnum,
} from '../../../../../../libs/shared/core/src/models/api/contactModel';
import {RecipientRolesEnum} from '../../../../../../libs/shared/core/src/models/api/recipientModel';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {getIsUserGlobalAdmin} from '../../../../../../libs/shared/core/src/store/data/access/commonAccessSelectors';
import {ResolveStateProps} from '../../../../../../libs/shared/core/src/store/helpers/commonReselectHelpers';
import {
  ResolveDispatchProps,
  wrapActionCreator,
} from '../../../../../../libs/shared/core/src/store/helpers/reduxHelpers';
import {createSelector, makeMapStateToProps} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {IntegrationTypeModel} from '../../../../../models/api/integrationTypesModel';
import {VisualRecipientModel} from '../../../../../models/api/visualRecipientModel';
import {Dispatch} from '../../../../../store/appStore';
import {getCompany} from '../../../../../store/data/company/companySelectors';
import {getHost} from '../../../../../store/data/dataSelectors';
import {
  dismissIntegrationInformationBannerAction,
  requestIntegrationAction,
} from '../../../../../store/data/localPreferences/localPreferencesActions';
import {composeMessageAction} from '../../../../../store/data/messages/draftMessagesActions';
import {getFlatAdmins, getUser} from '../../../../../store/data/teammates/teammatesSelectors';
import {navigateToLinkAction} from '../../../../../store/stateless/navigationActions';
import {IntegrationInformationBanner} from './integrationInformationBanner';

/*
 * Props.
 */

interface IntegrationInformationBannerContainerProps {
  integration: IntegrationTypeModel;
}

/*
 * Component.
 */

const getNotifiedAdmins = createSelector([getFlatAdmins], (admins) =>
  _(admins)
    .filter((a) => a.accountStatus === 'active')
    .sortBy((a) => a.createdAt)
    .take(5)
    .map((a) => {
      const adminRecipient: VisualRecipientModel = {
        contact: buildContactForHandle(ContactSourcesEnum.EMAIL, a.email),
        role: RecipientRolesEnum.TO,
        namespace: 'global',
      };
      return adminRecipient;
    })
    .value(),
);

const mapStateToProps = makeMapStateToProps<IntegrationInformationBannerContainerProps>()(() => ({
  isAdmin: getIsUserGlobalAdmin,
  host: getHost,
  company: getCompany,
  user: getUser,
  notifiedAdmins: getNotifiedAdmins,
}));

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    navigateToLink: wrapActionCreator(dispatch, navigateToLinkAction),
    dismissIntegration: wrapActionCreator(dispatch, dismissIntegrationInformationBannerAction),
    recordIntegration: wrapActionCreator(dispatch, requestIntegrationAction),
    composeMessage: wrapActionCreator(dispatch, composeMessageAction),
  };
}

function mergeProps(
  stateProps: ResolveStateProps<typeof mapStateToProps>,
  dispatchProps: ResolveDispatchProps<typeof mapDispatchToProps>,
  ownProps: IntegrationInformationBannerContainerProps,
) {
  const onEnableClick = () => {
    dispatchProps.navigateToLink(`/settings/integrations/native/edit/${ownProps.integration.id}/details`);
  };

  const onDismissClick = () => {
    dispatchProps.dismissIntegration(ownProps.integration.id);
  };

  const recordRequestedIntegration = () => {
    dispatchProps.recordIntegration(ownProps.integration.id);
  };

  const openComposer = () => {
    const {integration} = ownProps;
    const {host, notifiedAdmins, user} = stateProps;

    const subject = `Request for ${integration.name} integration to be enabled in Front`;
    const trustedHtml = buildRequestIntegrationTrustedHtml(integration, user, host);

    dispatchProps.composeMessage({
      subject,
      trustedHtml,
      recipients: notifiedAdmins,
      shouldHideSignature: true,
    });
  };

  return {
    ...ownProps,
    ...stateProps,
    onEnableClick,
    onDismissClick,
    recordRequestedIntegration,
    openComposer,
  };
}

export const IntegrationInformationBannerContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(IntegrationInformationBanner);

function buildRequestIntegrationTrustedHtml(
  integration: IntegrationTypeModel,
  user: TeammateModel,
  host: string,
): CleanHtml {
  const integrationSettingsUrl = `${host}/settings/integrations/native/edit/${integration.id}/details`;
  return iSolemnlySwearThisHtmlIsClean(
    ReactDOMServer.renderToStaticMarkup(
      <>
        <div>Hello,</div>
        <div>&nbsp;</div>
        <div>
          I would like to request that we enable the {integration.name} integration in Front, as this would be
          useful for our workflow.
        </div>
        <div>&nbsp;</div>
        <div>
          As an Admin of our Front account, you can click&nbsp;
          <a href={integrationSettingsUrl} rel="noopener noreferrer" target="_blank">
            here
          </a>
          &nbsp;to enable it.
        </div>
        <div>&nbsp;</div>
        <div>Thanks,</div>
        <div>{user.givenName}</div>
      </>,
    ),
  );
}
